import { CollapsibleSection } from '@/components/candidate-job-slider-v1/components/overview/offer/components/CollapsibleSection';
import { OfferDetailsSection } from '@/components/candidate-job-slider-v1/components/overview/offer/components/OfferDetailsSection';
import { useShowToast } from '@/hooks';
import { useAtsPermissions } from '@/hooks/useAtsPermissions';
import { ViewFile } from '@/components/ats/FileUpload/ViewFile';
import {
  useApproveRejectActionMutation,
  useLazyGetApprovalsByEntityQuery,
} from '@/store/query/approval-builder';
import { useLazyGetOfferByIdQuery } from '@/store/query/offer-Initiation';
import {
  Box,
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  Spinner,
  useDisclosure,
  Center,
  useBreakpointValue,
} from '@chakra-ui/react';
import { Avatar } from '@kula-ai/components';
import {
  ChevronDown,
  LinkExternal02,
  MinusCircle,
  X,
} from '@kula-ai/icons-core';
import { useState, useEffect } from 'react';
import { ApprovalFlowSteps } from '../ApprovalFlowSteps';
import { APPROVAL_NOTE_FORM_CONFIG } from '../ApprovalFlowSteps/constants';
import { Drawer } from '../drawer';
import { ModalWithForm } from '@/components/ats';
import { KText } from '@/ui-library/components';
import { useIsMobile } from '@/hooks/use-IsMobile';
import { MOBILE_BREAKPOINT } from '@/constants/mobile-breakpoints';
import { useAuth } from '@/hooks/use-auth';
import { useHistory } from 'react-router-dom';
import { getOfferDocumentFiles, type OfferFile } from '@/utils/offerUtils';
import { formatBytes } from '@/utils/format-bytes';
import type { ResolvedApprovalAttachment } from '@/components/contacts/candidate-job-drawer/tabs/current-job/details/OfferV1/components/offer-approvals/attachFilesApi';

type DocumentsFile = OfferFile | ResolvedApprovalAttachment;

export const OfferApprovalView = ({
  modalActions,
  offerId,
  onSuccess = () => {},
  hideApprovalStatus = false,
}) => {
  const isMobile = useIsMobile({ mobileBreakpoint: MOBILE_BREAKPOINT });
  const { isBootstraping } = useAuth();
  const history = useHistory();

  const rejectApproveFields = [
    {
      name: 'note',
      type: 'textArea',
      label: 'Notes',
      maxLength: 250,
      placeholder: 'Write your reason here',
    },
  ];

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const approverActions = useDisclosure();
  const modalSize = useBreakpointValue({ base: 'full', md: 'md' });

  const [getCurrentOffer, { data: currentOffer, isFetching: isOfferFetching }] =
    useLazyGetOfferByIdQuery({});

  const [
    getApprovalData,
    { data: currentApproval, isFetching: isCurrentApprovalLoading },
  ] = useLazyGetApprovalsByEntityQuery();

  const { currUserId } = useAtsPermissions();
  const statusMenuActions = useDisclosure();
  const {
    offer_status,
    candidate_id,
    candidate_name,
    requisition,
    ats_application_id,
    ats_job_id,
  } = currentOffer || {};

  // Prefer the offer's top-level ats_job_id / ats_application_id; fall back to the
  // requisition-group member for legacy responses missing the direct ids.
  const reqMember = requisition?.group?.members?.find(
    (m: any) => m?.id === requisition?.id
  );

  // Candidate link → the candidate's job page.
  const goToCandidateJob = () => {
    const jobId = ats_job_id || reqMember?.ats_job_id;
    if (!jobId) return;
    const appId = ats_application_id || reqMember?.ats_application_id;
    const query = appId
      ? `?appId=${appId}&currentView=stage&tab=candidates`
      : '';
    history.push(`/jobs/${jobId}${query}`);
    modalActions?.onClose();
  };

  // Docs for the approver: generated docs + attachments, or once e-signed the
  // consolidated signed offer + attachments (CORE-4774 / CORE-4794).
  const offerFiles = getOfferDocumentFiles(currentOffer);
  // CORE-5264 — the RESOLVED offer-approval selections (resume, scorecard PDF,
  // offer_document refs the recruiter picked). contract.yml AMENDMENT 2: a
  // dedicated `approval_attachments` field on the offer show payload — never
  // the pre-existing `attachments` array above (a different, older concept).
  const approvalAttachmentFiles: ResolvedApprovalAttachment[] = (
    currentOffer?.approval_attachments ?? []
  ).filter((file: ResolvedApprovalAttachment) => file?.url);
  const documentsFiles: DocumentsFile[] = [
    ...offerFiles,
    ...approvalAttachmentFiles,
  ];
  const isPendingApproval = offer_status === 'pending_approval';

  useEffect(() => {
    if (offerId) {
      getCurrentOffer({ id: offerId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId]);

  useEffect(() => {
    if (isPendingApproval) {
      getApprovalData({ id: offerId, type: 'offer' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId, currentOffer]);

  const currentActiveStep = currentApproval?.approval_steps.find(
    (step) => step?.status === 'in_progress'
  );
  const currentStepUsers =
    currentActiveStep?.approval_user_groups?.[0]?.approval_users
      .filter((user) => user?.approval_log === null)
      ?.map((userObj) => userObj?.user?.id);
  // Only check if user is approver when currUserId is available (not bootstrapping)
  const isCurrentUserApprover =
    !isBootstraping && currUserId && currentStepUsers?.includes(currUserId);

  const toast = useShowToast();

  const [approverAcceptAction, { isLoading: isApproveActionLoading }] =
    useApproveRejectActionMutation();

  const [approverRejectAction, { isLoading: isRejectActionLoading }] =
    useApproveRejectActionMutation();

  const handleDrawerSubmit = (values: { note?: string } = {}) => {
    const note = values?.note?.trim();
    approverAcceptAction({
      payload: {
        type: 'offer',
        entity_id: offerId,
        log_action: 'approve',
        ...(note && { note }),
      },
    }).then((response) => {
      if ('error' in response) {
        const message = response?.error?.[0];
        toast({
          title: `Unable to approve the offer`,
          description: message,
          status: 'error',
        });
      } else {
        toast({
          title: `Offer approved successfully`,
        });
        setShowApproveModal(false);
        if (isMobile) {
          history.push('/tasks/jobs/approvals');
          modalActions?.onClose();
          return;
        }
        modalActions?.onClose();
        onSuccess();
      }
    });
  };

  const handleApproveOnBehalf = (userId, values: { note?: string } = {}) => {
    const note = values?.note?.trim();
    approverAcceptAction({
      payload: {
        type: 'offer',
        entity_id: offerId,
        log_action: 'approve',
        user_id: userId,
        ...(note && { note }),
      },
    }).then((response) => {
      if ('error' in response) {
        const message = response?.error?.[0];
        toast({
          title: `Unable to approve the offer`,
          description: message,
          status: 'error',
        });
      } else {
        toast({
          title: `Offer approved successfully`,
        });
        approverActions?.onClose();
        statusMenuActions?.onClose();
        onSuccess();
        if (isMobile) {
          modalActions?.onClose();
        }
      }
    });
  };

  const handleReject = (values) => {
    approverRejectAction({
      payload: {
        type: 'offer',
        entity_id: offerId,
        log_action: 'reject',
        note: values?.note || '--',
      },
    }).then((response) => {
      if ('error' in response) {
        const message = response?.error?.[0];
        toast({
          title: `Unable to reject the offer`,
          description: message,
          status: 'error',
        });
      } else {
        toast({
          title: `Offer has been rejected`,
        });
        modalActions?.onClose();
        setShowRejectModal(false);
        onSuccess();
      }
    });
  };

  const moreCtaConfig = [
    {
      id: 'reject',
      Comp: () => {
        return (
          <Button
            variant="secondary"
            w={{ base: '100%', md: 'auto' }}
            onClick={() => {
              setShowRejectModal(true);
            }}
            loadingText="Updating"
            isLoading={isRejectActionLoading}
            disabled={isApproveActionLoading}
          >
            <MinusCircle
              size="1em"
              color="var(--kula-colors-ats-negative)"
              className="kula-icon"
              style={{ marginRight: '5px' }}
            />
            <KText size="sm-medium" color="primary-500">
              Reject
            </KText>
          </Button>
        );
      },
    },
  ];

  const renderStatus = (showBorder = true) => {
    return (
      <Flex
        gap="0.5em"
        alignItems="center"
        border={showBorder && '1px solid'}
        borderRadius="6px"
        w="fit-content"
        p="0.125em 0.5em"
        borderColor="ats-border-color"
      >
        <KText color="secondary-900" size="sm-semi-bold">
          Approval status
        </KText>
      </Flex>
    );
  };

  const RenderStatusAction = () => {
    const { isOpen, onOpen, onClose } = statusMenuActions;

    if (currentOffer?.offer_status === 'pending_approval')
      return (
        <Menu isOpen={isOpen} onClose={onClose}>
          <MenuButton
            as={Button}
            onClick={onOpen}
            w="fit-content"
            variant="ats-secondary"
            rightIcon={<ChevronDown size="1em" />}
          >
            {renderStatus(false)}
          </MenuButton>
          <MenuList
            w={{ base: '100%', md: '400px' }}
            maxH={{ base: '60vh', md: 'unset' }}
            overflowY="auto"
            boxShadow="drop-shadows-bottom"
            zIndex={99}
          >
            <ApprovalFlowSteps
              config={currentApproval}
              onClose={onClose}
              isLoading={isCurrentApprovalLoading}
              offerId={offerId}
              entityId={offerId}
              entityType="offer"
              onSuccess={() => {
                getApprovalData({ id: offerId, type: 'offer' });
              }}
              renderModalInParent={true}
              handleModalOpen={approverActions.onOpen}
              handleUserSelect={setSelectedUser}
            />
          </MenuList>
        </Menu>
      );
    else {
      return (
        <>
          {statusMap[currentOffer?.offer_status] && (
            // MenuButton reads Chakra's menu context, so it throws outside a
            // Menu — this status pill rendered bare and crashed the drawer.
            <Menu>
              <MenuButton
                as={Button}
                onClick={(event) => {
                  event?.stopPropagation();
                  onOpen();
                }}
                variant="ats-pending"
                borderColor={'transparent'}
                rightIcon={<ChevronDown size={16} color="#D98647" />}
                maxH="24px"
              >
                <KText color="#D98647" size="sm-semi-bold">
                  {statusMap[currentOffer?.offer_status]}
                </KText>
              </MenuButton>
            </Menu>
          )}
        </>
      );
    }
  };

  const statusMap = {
    draft: 'draft',
    approved_internal: 'offer approved',
    rejected_internal: 'offer rejected',
    candidate_email_in_progress: 'sending offer',
    candidate_email_failed: 'unable to send offer',
    awaiting_on_candidate: 'offer sent',
    accepted_by_candidate: 'accepted by candidate',
    rejected_by_candidate: 'rejected by candidate',
  };

  return (
    <>
      {modalActions?.isOpen && (
        <Drawer
          drawerActions={modalActions}
          hideCloseButton={isMobile}
          onDrawerSubmit={() => setShowApproveModal(true)}
          drawerContentProps={{
            minW: { base: '100%', md: '750px' },
            // Horizontal gutter lives on each section, not here: padding on the
            // content box would inset the header and clip the divider's ends.
            px: 0,
            py: { base: 0, md: 2 },
            borderTopLeftRadius: { base: 0, md: 'xl' },
          }}
          drawerBodyProps={{
            // Drawer body is the scroll container: override the default h="100%"
            // (which pushed the scrollable bottom off-screen) to fill remaining
            // space and scroll within it.
            overflow: 'auto',
            flex: '1 1 0%',
            minH: 0,
            h: 'auto',
          }}
          headerRightAdditionalComponent={
            !hideApprovalStatus && (
              <Flex alignItems="center" p="8px 24px">
                <RenderStatusAction />
              </Flex>
            )
          }
          disableCancel
          hasHeaderDivider
          headerTextProps={{ p: '1rem 1.5rem' }}
          drawerFooterProps={{ px: '1.5rem' }}
          moreCtaConfig={moreCtaConfig}
          showHeader={!isMobile}
          headerText={
            <Flex alignItems="center" gap="8px" flexWrap="wrap">
              <KText variant="heading_2">Approve offer for</KText>
              {candidate_id && (
                <Flex
                  alignItems="center"
                  gap="6px"
                  cursor="pointer"
                  onClick={goToCandidateJob}
                >
                  <Avatar size={18} shape="square" name={candidate_name} />
                  <KText
                    size="md-medium"
                    color="primary-600"
                    _hover={{ textDecoration: 'underline' }}
                  >
                    {candidate_name}
                  </KText>
                  <LinkExternal02
                    size={16}
                    color="var(--kula-colors-primary-600)"
                    className="kula-icon"
                  />
                </Flex>
              )}
            </Flex>
          }
          showFooter={!isMobile && isCurrentUserApprover && isPendingApproval}
          ctaProps={{
            loadingText: 'Updating',
            isLoading: isApproveActionLoading,
            disabled: isRejectActionLoading,
            w: { base: '100%', md: 'auto' },
          }}
          ctaLabel="Approve offer"
        >
          {isMobile && (
            <Box position="relative" flexShrink={0}>
              <Center
                w="48px"
                h="48px"
                left="12px"
                top="3px"
                pos="absolute"
                bg="#fff"
                borderRadius="full"
                cursor="pointer"
                onClick={modalActions?.onClose}
                zIndex={1}
              >
                <Icon as={X} w="20px" h="20px" />
              </Center>

              <Box pl="64px" pr="16px" py="12px">
                <Flex alignItems="center" gap="8px" flexWrap="wrap">
                  <KText size="lg-semi-bold" color="secondary-900">
                    Approve offer for
                  </KText>
                  {candidate_id && (
                    <Flex
                      alignItems="center"
                      gap="6px"
                      cursor="pointer"
                      onClick={goToCandidateJob}
                    >
                      <Avatar size={24} shape="square" name={candidate_name} />
                      <KText size="md-medium" color="primary-600">
                        {candidate_name}
                      </KText>
                      <LinkExternal02
                        size={16}
                        color="var(--kula-colors-primary-600)"
                        className="kula-icon"
                      />
                    </Flex>
                  )}
                </Flex>
              </Box>
            </Box>
          )}

          <Flex
            flexDir="column"
            gap={{ base: '6px', md: '10px' }}
            p={{ base: '8px', md: '8px 24px' }}
          >
            {isOfferFetching || (isPendingApproval && isBootstraping) ? (
              // Full-page loader: minH centers the spinner (h="100%" didn't resolve
              // without a definite parent height and rendered small up top).
              <Flex
                minH="80vh"
                w="100%"
                alignItems="center"
                justifyContent="center"
              >
                <Spinner color="primary-800" size="xl" />
              </Flex>
            ) : (
              <>
                <CollapsibleSection title="Details" defaultIsOpen={true}>
                  <OfferDetailsSection currentOffer={currentOffer} sectioned />
                </CollapsibleSection>
                {documentsFiles.length > 0 && (
                  <CollapsibleSection title="Documents" defaultIsOpen={true}>
                    <Flex flexDir="column" gap="8px" p="12px">
                      {documentsFiles.map((file, i) => (
                        <ViewFile
                          // Neither source carries a stable id (OfferFile has
                          // none at all; ResolvedApprovalAttachment has no
                          // ref_id — see cases-found.md N7), so key on the
                          // ref_type/filename pair. NOT on `url`: it is a
                          // signed blob URL that is re-presigned on every
                          // GET show, so keying on it remounts every row on
                          // every refetch and closes any open preview.
                          key={`${'ref_type' in file ? file.ref_type : 'offer_document'}-${file.filename || i}`}
                          file={{
                            url: file.url,
                            filename:
                              file.filename ||
                              `Offer document${offerFiles.length > 1 ? ` ${i + 1}` : ''}`,
                            content_type: file.content_type,
                          }}
                          // amendment_6_pregenerate_scorecard_pdf removed the nil
                          // byte_size case (was: amendment_3a's lazily-rendered
                          // scorecard). formatBytes already no-ops safely for a
                          // missing byte_size (offerFiles' OfferFile carries none) —
                          // no guard needed either way.
                          subtitle={formatBytes(
                            'byte_size' in file ? file.byte_size : undefined
                          )}
                          showIcon={true}
                          showDelete={false}
                          showDownload={true}
                          showPreview={true}
                          hideTextReference={true}
                        />
                      ))}
                    </Flex>
                  </CollapsibleSection>
                )}
              </>
            )}
          </Flex>

          {/* --- MOBILE FOOTER BUTTONS --- */}
          {isMobile && isCurrentUserApprover && isPendingApproval && (
            <Flex
              position="sticky"
              bottom="0"
              w="100%"
              p="12px"
              bg="white"
              boxShadow="drop-shadows-bottom"
              gap="8px"
            >
              {/* Reject Button */}
              <Button
                flex="1"
                variant="mobile-danger-outline"
                onClick={() => setShowRejectModal(true)}
                isLoading={isRejectActionLoading}
                disabled={isApproveActionLoading}
              >
                Reject
              </Button>

              <Button
                flex="1"
                variant="mobile-primary-lg"
                onClick={() => setShowApproveModal(true)}
                isLoading={isApproveActionLoading}
                disabled={isRejectActionLoading}
              >
                Approve Offer
              </Button>
            </Flex>
          )}
        </Drawer>
      )}
      {showRejectModal && (
        <ModalWithForm
          isOpen={true}
          onClose={() => {
            setShowRejectModal(false);
          }}
          headerText={'Reason for rejection?'}
          subHeaderText={
            'Provide detailed feedback on why you are rejecting this offer'
          }
          primaryCtaText="Reject"
          formConfig={rejectApproveFields}
          handleSubmit={handleReject}
          isCtaLoading={isRejectActionLoading}
          formContainerStyles={{
            m: { base: '0', md: '-10px 0' },
          }}
          skipDirtyCheck={true}
        />
      )}
      {showApproveModal && (
        <ModalWithForm
          isOpen={true}
          onClose={() => setShowApproveModal(false)}
          headerText="Approve offer?"
          subHeaderText="Add an optional reason for approving this offer"
          primaryCtaText="Approve"
          formConfig={APPROVAL_NOTE_FORM_CONFIG}
          handleSubmit={handleDrawerSubmit}
          isCtaLoading={isApproveActionLoading}
          formContainerStyles={{
            m: { base: '0', md: '-10px 0' },
          }}
          skipDirtyCheck={true}
        />
      )}
      {approverActions?.isOpen && (
        // ModalWithForm hosts the Reason field itself: importing FormBuilder
        // here closes an import cycle back through the components/ats barrel.
        <ModalWithForm
          isOpen={true}
          onClose={approverActions?.onClose}
          headerText={`Approve on behalf of ${selectedUser?.user?.name || ''}`}
          modalSize={modalSize}
          // ModalWithForm renders this inside a <p>, so keep it inline.
          subHeaderText={
            <>
              Approving for {selectedUser?.user?.name} will move the approval
              flow forward as though they did it.
              <br />
              This is permanent and cannot be reversed.
            </>
          }
          primaryCtaText="Approve"
          formConfig={APPROVAL_NOTE_FORM_CONFIG}
          handleSubmit={(values: { note?: string }) => {
            if (selectedUser?.user?.id) {
              handleApproveOnBehalf(selectedUser.user.id, values);
            }
          }}
          isCtaLoading={isApproveActionLoading}
          formContainerStyles={{
            m: { base: '0', md: '-10px 0' },
          }}
          skipDirtyCheck={true}
        />
      )}
    </>
  );
};
